import { useEffect } from 'react';
import { Navigate, Route, Routes, useLocation, useRoutes } from 'react-router-dom';
import routes from 'tempo-routes';

import { AdminProtectedRoute } from './AdminProtectedRoute';
import { HomeHQProtectedRoute } from './HomeHQProtectedRoute';
import { HomeRedirect } from './HomeRedirect';
import { ProtectedRoute } from './ProtectedRoute';
import { SuperAdminProtectedRoute } from './SuperAdminProtectedRoute';

import { AdminDashboardLayout } from '@/components/dashboard/AdminDashboardLayout';
import { DashboardLayout } from '@/components/dashboard/DashboardLayout';
import {
  HomeownerHub,
  RecordsPanel,
  CalendarPanel,
  IssuesPanel,
} from '@/components/home/HomeownerHub';
import { VendorDirectoryPanel } from '@/components/home/VendorDirectoryPanel';
import Login from '@/components/login/Login';
import ConfirmPasswordReset from '@/components/password-reset/ConfirmPasswordReset';
import PasswordResetRequestSent from '@/components/password-reset/PasswordResetRequestSent';
import RequestPasswordReset from '@/components/password-reset/RequestPasswordReset';
import EmailVerificationSent from '@/components/register/EmailVerificationSent';
import Register from '@/components/register/Register';
import Uploader from '@/components/uploader/Uploader';
import UpdateUserProfile from '@/components/user/UpdateUserProfile';
import { useAuthContext } from '@/context/AuthProvider';
import { usePropertiesContext } from '@/context/PropertiesProvider';
import { useClientGroups } from '@/hooks/useClientGroups';
import { useUserRole } from '@/hooks/useUserRole';
import { AcquisitionsTab } from '@/pages/admin/AcquisitionsTab';
import { InfracostsTab } from '@/pages/admin/InfracostsTab';
import { KpisTab } from '@/pages/admin/KpisTab';
import { LogicTab } from '@/pages/admin/LogicTab';
import { MessagesOversight } from '@/pages/admin/MessagesOversight';
import { PropertiesTab } from '@/pages/admin/PropertiesTab';
import { RelationshipsTab } from '@/pages/admin/RelationshipsTab';
import { UsersTab } from '@/pages/admin/UsersTab';
import { AdvisorClientsPage } from '@/pages/dashboard/AdvisorClientsPage';
import { CalculatorsView } from '@/pages/dashboard/CalculatorsView';
import { ChatsPage } from '@/pages/dashboard/ChatsPage';
import { ConditionScoreView } from '@/pages/dashboard/ConditionScoreView';
import { ContextScoreView } from '@/pages/dashboard/ContextScoreView';
import { HOADetailView } from '@/pages/dashboard/HOADetailView';
import { InsightsView } from '@/pages/dashboard/InsightsView';
import { InspectionReportView } from '@/pages/dashboard/InspectionReportView';
import { MarketInsightsView } from '@/pages/dashboard/MarketInsightsView';
import { PlansView } from '@/pages/dashboard/PlansView';
import { PropertiesPageNew } from '@/pages/dashboard/PropertiesPageNew';
import { SellerDisclosuresView } from '@/pages/dashboard/SellerDisclosuresView';
import { SettingsView } from '@/pages/dashboard/SettingsView';
import { SummaryView } from '@/pages/dashboard/SummaryView';
import { TodayView } from '@/pages/dashboard/TodayView';
import { DesignLibrary } from '@/pages/dev/DesignLibrary';
import { MaterialsCompare } from '@/pages/dev/MaterialsCompare';
import { OverviewPreview } from '@/pages/dev/OverviewPreview';
import AcceptInvite from '@/pages/invite/AcceptInvite';
import OAuthCallback from '@/pages/OAuthCallback';
import { AdvisorOnboarding } from '@/pages/onboarding/AdvisorOnboarding';
import ClaimHomeAddress from '@/pages/onboarding/ClaimHomeAddress';
import PurchaseComplete from '@/pages/PurchaseComplete';
import { SharedRFQView } from '@/pages/SharedRFQView';
import { isUserProfileIncomplete } from '@/utils/user';

function DashboardRedirect() {
  const { user } = useAuthContext();
  const { isAdvisor } = useUserRole();
  const { hasOwnedProperties, isInitialFetchingProperties } = usePropertiesContext();
  const { groups, isLoading: isLoadingGroups } = useClientGroups();

  if (isAdvisor) return <Navigate to="clients" replace />;
  // Wait for properties and group memberships before deciding the destination.
  if (isInitialFetchingProperties || isLoadingGroups) return null;
  if (hasOwnedProperties) return <Navigate to="home-hub" replace />;
  // Independent owners without a property enter home-hub directly — the
  // self-serve onboarding modal there walks them through claiming a home,
  // completing their profile, and uploading a report in one flow.
  if (groups.length === 0) return <Navigate to="home-hub" replace />;
  // Advisor-managed buyers continue through the deal flow, but still need a
  // complete profile first — Login/OAuthCallback only enforce this for staff
  // now, so it's caught here instead.
  if (user && isUserProfileIncomplete(user)) {
    return <Navigate to="/user/update-profile" replace state={{ hideTopNav: true }} />;
  }
  return <Navigate to="summary" replace />;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

export const Router = () => (
  <>
    <ScrollToTop />
    <Routes>
      <Route path="/login" element={<Login />} />
      {import.meta.env.DEV && <Route path="/dev/library" element={<DesignLibrary />} />}
      {import.meta.env.DEV && <Route path="/dev/compare" element={<MaterialsCompare />} />}
      {import.meta.env.DEV && <Route path="/dev/overview" element={<OverviewPreview />} />}
      <Route path="/register" element={<Register />} />
      <Route path="/email-verification-sent" element={<EmailVerificationSent />} />
      <Route path="/password-reset/request" element={<RequestPasswordReset />} />
      <Route path="/password-reset/request-sent" element={<PasswordResetRequestSent />} />
      <Route path="/password-reset/confirm" element={<ConfirmPasswordReset />} />
      <Route path="/oauth-callback" element={<OAuthCallback />} />
      <Route path="/accept-invite" element={<AcceptInvite />} />
      <Route path="/purchase-complete" element={<PurchaseComplete />} />
      <Route path="/rfq/share/:token" element={<SharedRFQView />} />
      <Route element={<ProtectedRoute />}>
        <Route path="/" element={<HomeRedirect />} />

        {/* Dashboard routes with nested layout */}
        <Route path="/dashboard" element={<DashboardLayout />}>
          <Route index element={<DashboardRedirect />} />
          <Route path="summary" element={<SummaryView />} />
          <Route path="inbox" element={<ChatsPage />} />
          <Route element={<HomeHQProtectedRoute />}>
            <Route path="home-hub" element={<HomeownerHub />} />
            <Route path="home-hub/issues" element={<IssuesPanel />} />
            <Route
              path="home-hub/repair-plan"
              element={<InsightsView renderWithoutData addTopSpacing />}
            />
            <Route path="home-hub/records" element={<RecordsPanel />} />
            <Route path="home-hub/directory" element={<VendorDirectoryPanel />} />
            <Route path="home-hub/vendors" element={<VendorDirectoryPanel />} />
            <Route path="home-hub/calendar" element={<CalendarPanel />} />
          </Route>
          <Route path="condition-score" element={<ConditionScoreView />} />
          <Route path="context-score" element={<ContextScoreView />} />
          <Route path="hoa" element={<HOADetailView />} />
          <Route path="seller-disclosures" element={<SellerDisclosuresView />} />
          <Route path="inspection-report" element={<InspectionReportView />} />
          <Route path="repair-planner" element={<InsightsView />} />
          {/* Old URL, kept working for anything bookmarked/linked before the
              nav label was renamed to "Repair Planner" — path stayed
              "insights" until now. `replace` so it doesn't leave the old
              URL in browser back-history. */}
          <Route path="insights" element={<Navigate to="/dashboard/repair-planner" replace />} />
          <Route path="market-insights" element={<MarketInsightsView />} />
          <Route path="properties" element={<PropertiesPageNew />} />
          <Route path="clients" element={<AdvisorClientsPage />} />
          <Route path="today" element={<TodayView />} />
          <Route path="calculators" element={<CalculatorsView />} />
          <Route path="settings" element={<SettingsView />} />
          {/* Outside HomeHQProtectedRoute: lapsed users must reach it to resubscribe. */}
          <Route path="plans" element={<PlansView />} />
        </Route>

        <Route path="/onboarding" element={<AdvisorOnboarding />} />
        <Route path="/claim-home" element={<ClaimHomeAddress />} />
        <Route path="/user/update-profile" element={<UpdateUserProfile />} />
        <Route path="/uploader" element={<Uploader />} />
      </Route>

      {/* Admin routes - requires Super Admin or Advisor role */}
      <Route element={<AdminProtectedRoute />}>
        <Route path="/admin" element={<AdminDashboardLayout />}>
          <Route index element={<Navigate to="users" replace />} />
          <Route path="users" element={<UsersTab />} />
          <Route path="properties" element={<PropertiesTab />} />
          <Route path="relationships" element={<RelationshipsTab />} />
          <Route path="advisor-clients" element={<AdvisorClientsPage />} />
          <Route path="inbox" element={<MessagesOversight />} />
          <Route path="settings" element={<SettingsView />} />
          <Route element={<SuperAdminProtectedRoute />}>
            <Route path="logic" element={<LogicTab />} />
            <Route path="logic/:tab" element={<LogicTab />} />
            <Route path="infracosts" element={<InfracostsTab />} />
          </Route>
          <Route path="acquisitions" element={<AcquisitionsTab />} />
          <Route path="kpis" element={<KpisTab />} />
        </Route>
      </Route>
    </Routes>

    {import.meta.env.VITE_TEMPO === 'true' && useRoutes(routes)}
  </>
);
